import { ref } from 'vue';
import { $api } from '@/main';
import { useClientAuthStore } from '@/Auth/stores/clientAuthStore';

export function useAuth() {
  const clientAuth = useClientAuthStore();
  const loading = ref(false);
  const error = ref('');
  const errors = ref<Record<string, string>>({});
  const verificationStep = ref(false);
  
  // Initialisation correcte du userId
  const storedUserId = localStorage.getItem('pending_user_id');
  const userId = ref<number | null>(storedUserId ? parseInt(storedUserId) : null);

  const register = async (formData: any) => {
    loading.value = true;
    error.value = '';
    errors.value = {};

    try {
      const response = await $api.post('/api/register', formData);
      
      if (response.data.success) {
        userId.value = response.data.user_id;
        localStorage.setItem('pending_user_id', String(response.data.user_id));
        verificationStep.value = true;
        return { success: true, requiresVerification: true };
      }
      return { success: false, message: response.data.message };
    } catch (err: any) {
      if (err.response?.data?.errors) {
        errors.value = err.response.data.errors;
        return { success: false, message: Object.values(errors.value).join(', ') };
      }
      const message = err.response?.data?.message || 'Erreur lors de l\'inscription';
      error.value = message;
      return { success: false, message };
    } finally {
      loading.value = false;
    }
  };

  const verifyCode = async (code: string) => {
    loading.value = true;
    error.value = '';

    const storedId = localStorage.getItem('pending_user_id');
    const userIdToUse = userId.value || (storedId ? parseInt(storedId) : null);
    
    if (!userIdToUse) {
      error.value = 'Session expirée. Veuillez vous réinscrire.';
      loading.value = false;
      return { success: false, message: 'Session expirée' };
    }

    try {
      const response = await $api.post('/api/verify-code', {
        user_id: userIdToUse,
        code: code
      });

      if (response.data.success) {
        localStorage.setItem('client_token', response.data.token);
        localStorage.setItem('client_user', JSON.stringify(response.data.user));
        localStorage.removeItem('pending_user_id');
        
        clientAuth.state.user = response.data.user;
        clientAuth.state.isAuthenticated = true;
        clientAuth.state.token = response.data.token;
        
        $api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        
        verificationStep.value = false;
        userId.value = null;
        return { success: true, user: response.data.user };
      }
      const message = response.data.message || 'Code invalide';
      error.value = message;
      return { success: false, message };
    } catch (err: any) {
      const message = err.response?.data?.message || 'Code invalide';
      error.value = message;
      return { success: false, message };
    } finally {
      loading.value = false;
    }
  };

  const resendCode = async () => {
    loading.value = true;
    error.value = '';
    
    const storedId = localStorage.getItem('pending_user_id');
    const userIdToUse = userId.value || (storedId ? parseInt(storedId) : null);
    
    if (!userIdToUse) {
      error.value = 'Session expirée';
      loading.value = false;
      return { success: false, message: 'Session expirée' };
    }
    
    try {
      await $api.post('/api/resend-code', { user_id: userIdToUse });
      loading.value = false;
      return { success: true, message: 'Un nouveau code a été envoyé' };
    } catch (err: any) {
      const message = err.response?.data?.message || 'Erreur lors du renvoi du code';
      error.value = message;
      loading.value = false;
      return { success: false, message };
    }
  };

  const login = async (identifier: string, password: string) => {
    loading.value = true;
    error.value = '';
    errors.value = {};

    try {
      const response = await $api.post('/api/login', { identifier, password });
      
      if (response.data.success) {
        localStorage.setItem('client_token', response.data.token);
        localStorage.setItem('client_user', JSON.stringify(response.data.user));
        clientAuth.state.user = response.data.user;
        clientAuth.state.isAuthenticated = true;
        clientAuth.state.token = response.data.token;
        $api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        loading.value = false;
        return { success: true, user: response.data.user };
      }
      loading.value = false;
      return { success: false, message: response.data.message };
    } catch (err: any) {
      if (err.response?.data?.requires_verification) {
        const userIdFromError = err.response.data.user_id;
        if (userIdFromError) {
          localStorage.setItem('pending_user_id', String(userIdFromError));
          userId.value = userIdFromError;
        }
        loading.value = false;
        return { 
          success: false, 
          requiresVerification: true,
          message: 'Veuillez vérifier votre email'
        };
      }
      const message = err.response?.data?.message || 'Erreur de connexion';
      error.value = message;
      loading.value = false;
      return { success: false, message };
    }
  };

  const forgotPassword = async (email: string) => {
    loading.value = true;
    error.value = '';

    try {
      const response = await $api.post('/api/forgot-password', { email });
      loading.value = false;
      if (response.data.success) {
        return { success: true, message: 'Email envoyé' };
      }
      const message = response.data.message || 'Email non trouvé';
      error.value = message;
      return { success: false, message };
    } catch (err: any) {
      const message = err.response?.data?.message || 'Email non trouvé';
      error.value = message;
      loading.value = false;
      return { success: false, message };
    }
  };

  const resetPassword = async (email: string, token: string, password: string, passwordConfirmation: string) => {
    loading.value = true;
    errors.value = {};
    error.value = '';

    try {
      const response = await $api.post('/api/reset-password', {
        email,
        token,
        password,
        password_confirmation: passwordConfirmation
      });
      
      loading.value = false;
      if (response.data.success) {
        return { success: true, message: 'Mot de passe réinitialisé' };
      }
      const message = response.data.message || 'Erreur lors de la réinitialisation';
      error.value = message;
      return { success: false, message };
    } catch (err: any) {
      if (err.response?.data?.errors) {
        errors.value = err.response.data.errors;
        loading.value = false;
        return { success: false, message: Object.values(errors.value).join(', ') };
      }
      const message = err.response?.data?.message || 'Erreur lors de la réinitialisation';
      error.value = message;
      loading.value = false;
      return { success: false, message };
    }
  };

  return {
    loading,
    error,
    errors,
    verificationStep,
    userId,
    register,
    verifyCode,
    resendCode,
    login,
    forgotPassword,
    resetPassword
  };
}